import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  CmdCalendar,
  CmdLabel,
  CmdModal,
  CmdModalFooterButton,
} from '@commander-services/gui-components';
import dayjs from 'dayjs';
import { AxiosResponse } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import PermissionsService, { PERMISSION_PERIOD_LOCK } from '../../Services/PermissionsService';
import useUrlQueryParams from '../../hooks/useUrlQueryParams';
import usePageWithForm from '../../hooks/usePageWithForm';
import { getRequest, putRequest } from '../../Services/HttpService';
import { IValidationApiResponse } from '../../interfaces/apiResponse';
import { IInput } from '../../Services/FormikValidatorService';
import { transformErrors, validateFields } from '../../Services/HookFormService';
import useAnalytics from '../../hooks/useAnalytics';

const PERIOD_LOCK_MODAL_FORM_KEY = 'period-lock-modal-form';

interface IFormProps {
  closeCallback: () => void;
  updateTable: () => void;
  id: number;
  lockDate: Date | null;
  periodDates?: string;
}

interface IRule {
  param: string;
  value: boolean;
  message: string;
}

export default function ModalForm(props: IFormProps) {
  usePageWithForm();
  const { formatMessage: f } = useIntl();
  const [isLoaderActive, setIsLoaderActive] = React.useState<boolean>(false);
  const [datepickerRules, setDatepickerRules] = React.useState<IRule[]>([]);
  const [data, setData] = React.useState<IInput[]>([
    {
      indexId: 'lockDate',
      title: f({ id: 'alarms.section.recipient.name' }),
      type: 'date',
      readonly: false,
      disabled: false,
      value: props.lockDate || '',
      placeholder: f({ id: 'form.search' }),
      rules: [
        {
          param: 'required',
          value: true,
          message: f({ id: 'form.required' }),
        },
      ],
    },
  ]);
  const queryUrl = useUrlQueryParams();
  const customerValueUrl = queryUrl.get('customerId');
  const { trackEvent } = useAnalytics();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    setError,
    trigger,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      lockDate: props.lockDate || '',
    },
    resolver: async (formData) => {
      const formValidation: IValidationApiResponse | null = {
        lockDate: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
        ],
      };

      const validateErros = validateFields(formData, formValidation);
      const formErrors = transformErrors(validateErros);

      return {
        values: formErrors && Object.keys(formErrors).length ? {} : formData, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const fields: IInput[] = Object.assign(data);

  const getValidation = async () => {
    setIsLoaderActive(true);
    const response: AxiosResponse | false = await getRequest(`/v1/period-lock/${props.id}/form`, {
      returnAxiosResponse: true,
    });
    setIsLoaderActive(false);

    if (
      (response as AxiosResponse).data &&
      (response as AxiosResponse).status === 200 &&
      (response as AxiosResponse).data.form
    ) {
      const inputs = (response as AxiosResponse).data.form;
      Object.values(fields).forEach((item) => {
        Object.keys(inputs).forEach((input) => {
          if (item.indexId === input) {
            if (inputs[item.indexId]) {
              const rules = inputs[item.indexId];
              setDatepickerRules(rules);
              item.rules = rules;
              const minDateData = rules.find((i) => i.param === 'minDate');
              if (
                minDateData &&
                minDateData.value &&
                props.lockDate &&
                minDateData.value > props.lockDate
              ) {
                item.value = minDateData.value;
              }
            }
          }
        });
      });
      setData({
        ...data,
        ...fields,
      });
    }
  };

  const { isLoading: isLoadingValidation } = useQuery({
    queryKey: [PERIOD_LOCK_MODAL_FORM_KEY],
    queryFn: () => getValidation(),
    staleTime: 9000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const handleOnSubmit = async (values) => {
    trackEvent([`period lock form`, 'click', 'Submit form']);
    if (errors.lockDate) {
      return;
    }
    setIsLoaderActive(true);
    const response: AxiosResponse | false = await putRequest(
      `v1/period-lock/${props.id}/edit`,
      values,
      {
        returnAxiosResponse: true,
      }
    );
    setIsLoaderActive(false);

    if ((response as AxiosResponse).data && (response as AxiosResponse).status === 200) {
      props.closeCallback();
      props.updateTable();
    }
  };

  const buttons: CmdModalFooterButton[] = PermissionsService.hasAccess(
    PERMISSION_PERIOD_LOCK,
    ['update'],
    customerValueUrl ? Number(customerValueUrl) : null
  )
    ? [
        {
          id: 'close-modal',
          type: 'button',
          className: 'e-button',
          title: f({ id: 'alarms.modal.rule.button.close' }),
          tooltip: f({ id: 'alarms.modal.recipient.close.tooltip' }),
          closeCallback: props.closeCallback,
        },
        {
          id: 'periodlock-form-submit',
          type: 'submit',
          className: 'e-button e-button--gray',
          title: f({ id: 'alarms.modal.recipient.save' }),
          tooltip: f({ id: 'alarms.modal.recipient.save.tooltip' }),
          submitCallback: handleSubmit(handleOnSubmit),
        },
      ]
    : [
        {
          id: 'close-modal',
          type: 'button',
          className: 'e-button',
          title: f({ id: 'alarms.modal.rule.button.close' }),
          tooltip: f({ id: 'alarms.modal.recipient.close.tooltip' }),
          closeCallback: props.closeCallback,
        },
      ];

  const currentDate = new Date(dayjs(new Date()).format('YYYY-MM-DD'));
  const endDate = new Date(dayjs(new Date()).add(3, 'year').format('YYYY-MM-DD'));

  const allowedRange = props.lockDate
    ? [new Date(dayjs(props.lockDate).format('YYYY-MM-DD')), endDate]
    : [currentDate, endDate];

  const isLoading = isLoadingValidation || isLoaderActive;

  return (
    <CmdModal
      id="period-lock-form-modal"
      title={`${f({ id: 'table_list.columns.periodLocks.action_edit_lockAfter' })} ${
        props.periodDates
      }`}
      footerButtons={buttons}
      loader={isLoading}
    >
      {PermissionsService.hasAccess(
        PERMISSION_PERIOD_LOCK,
        ['update'],
        customerValueUrl ? Number(customerValueUrl) : null
      ) ? (
        <Controller
          name="lockDate"
          control={control}
          render={({ field }) => (
            <CmdCalendar
              {...field}
              id="lockDate"
              label={f({ id: 'table_list.columns.lockDate' })}
              required={true}
              rules={datepickerRules || undefined}
              allowedRange={allowedRange}
              onChangeCallback={(value) => {
                setValue('lockDate', value as Date);
                trigger('lockDate');
                trackEvent([`period lock form`, 'change', 'Change lock date']);
              }}
              dateFormat="dd.MM.yyyy"
              error={errors.lockDate?.message}
              touched={true}
            />
          )}
        />
      ) : (
        <CmdLabel
          id="lockDate"
          title={f({ id: 'table_list.columns.lockDate' })}
          value={dayjs(fields[0].value).format('DD.MM.YYYY HH:mm')}
          areLabels={true}
        />
      )}
    </CmdModal>
  );
}
